"use client";

import { Link, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";

import { useAiUsage } from "@/app/providers/AiUsageProvider";
import { AiCreditLimitBanner } from "@/components/shared/AiCreditLimitBanner";
import { Dialog } from "@/components/shared/Dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface ImportUrlModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (ingredients: string[]) => void;
}

export function ImportUrlModal({ isOpen, onClose, onImport }: ImportUrlModalProps) {
  const t = useTranslations("ingredients");
  const tCommon = useTranslations("common");
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const aiUsage = useAiUsage();
  const creditsExhausted = aiUsage.loaded && aiUsage.remaining <= 0;

  const handleSubmit = async () => {
    if (!url.trim()) {
      toast.error(t("urlRequired"));
      return;
    }

    try {
      new URL(url);
    } catch {
      toast.error(t("invalidUrl"));
      return;
    }

    // The limit banner under the field already says why.
    if (aiUsage.loaded && aiUsage.remaining <= 0) return;

    setIsLoading(true);

    try {
      const response = await fetch("/api/import-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Over the weekly limit: the limit banner shows under the field in place of a toast.
        if (data.error === "aiUsageLimitReached") {
          aiUsage.set(aiUsage.limit, aiUsage.limit);
          return;
        }
        // A server error names an ingredients or a common message.
        const key = String(data.error) as never;
        toast.error(
          t.has(key) ? t(key) : tCommon.has(key) ? tCommon(key) : t("failedToExtractIngredients")
        );
        return;
      }

      if (data.ingredients && data.ingredients.length > 0) {
        if (data.usage != null && data.limit != null) {
          aiUsage.set(data.usage, data.limit);
        }

        toast.success(t("ingredientsImported", { count: data.ingredients.length }));
        onImport(data.ingredients);
        handleClose();
      } else {
        toast.error(t("noIngredientsFound"));
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error(t("failedToExtractIngredients"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setUrl("");
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      disableOutsideClick={isLoading}
      title={t("importFromUrl")}
      description={t("importFromUrlDescription")}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !url.trim() || (aiUsage.loaded && aiUsage.remaining === 0)}
          >
            {isLoading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t("importing")}
              </>
            ) : (
              <>
                <Link className="size-4" />
                {t("import")}
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {/* The limit banner sits first in every AI spot, on web and iOS (Jeremy, 2026-10-10). */}
        {creditsExhausted && <AiCreditLimitBanner />}
        <Input
          id="recipe-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={t("recipeUrlPlaceholder")}
          disabled={isLoading || creditsExhausted}
          onKeyDown={(e) => e.key === "Enter" && !isLoading && !creditsExhausted && handleSubmit()}
        />
        <p className="text-xs text-muted-foreground">{t("importIntentExplanation")}</p>
      </div>
    </Dialog>
  );
}
